import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormGroup,
  Modal,
  Paper,
  Select,
  Switch,
  TextField,
} from "@mui/material";
import { Pencil } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useAuth from "../contexts/UserContext.tsx";
import axiosInstance from "../Axios.jsx";
import { toast } from "sonner";
import { PulseLoader } from "react-spinners";

export const EditIndividualDashboardUserModal = ({ userDetails }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [newUserData, setNewUserData] = useState({
    ...userDetails,
    user_fname: userDetails.user_fname,
    user_lname: userDetails.user_lname,
    view_access_list: {
      isEvaluator: userDetails.view_access_list.isEvaluator,
      isWrite: userDetails.view_access_list.isWrite,
    },
  });

  const [viewAccessList, setViewAccessList] = useState(
    JSON.parse(userDetails.view_access_list)
  );
  const selectedDistrict = viewAccessList.district.split(",");
  const selectedSchoolType = viewAccessList.school_types.split(",");

  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(
    parseInt(viewAccessList.schoolID)
  );
  const [modal, setModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const getSchools = async () => {
    setLoading(true);
    axiosInstance
      .get(
        `getSchools?district="${selectedDistrict[0]}"&school_type="${selectedSchoolType[0]}"&auth_token=${user.auth_token}`
      )
      .then((res) => {
        setSchools(res.data.data.data);
      })
      .catch((err) => {
        toast.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleChange = (event) => {
    setViewAccessList({
      ...viewAccessList,
      [event.target.name]: event.target.checked,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newUserData.user_fname === "" || newUserData.user_lname === "") {
      toast.error("Please fill all fields");
      return;
    } else if (viewAccessList.schoolID === null) {
      toast.error("Please select a school");
      return;
    }

    //I don't know from where school_id is coming from. Too late at night to debug.
    const { school_id, ...restUserData } = newUserData;

    console.log({ ...restUserData, view_access_list: viewAccessList });
    await axiosInstance
      .post("/updateDashboardUser", {
        ...restUserData,
        schoolID: viewAccessList.schoolID,
        view_access_list: viewAccessList,
        auth_token: user.auth_token,
      })
      .then(() => {
        toast.success("User updated successfully");
        window.location.reload();
      })
      .catch((err) => {
        toast.error(err.message);
      });
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user.user_type !== "admin") {
      return navigate("/");
    }
    if (modal) {
      getSchools();
      setSelectedSchool(parseInt(viewAccessList.schoolID));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, modal]);

  return (
    <div className="h-full">
      <button
        onClick={() => setModal(true)}
        className="bg-[#4CAF50] hover:bg-green-600 text-white font-semibold py-2 px-2 rounded focus:outline-none focus:shadow-outline-green"
      >
        <Pencil />
      </button>
      <Modal
        open={modal}
        onClose={() => setModal(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Paper elevation={3} className="w-fit mx-auto mt-10 px-10 max-w-lg">
          <h1 className="text-center p-4 font-semibold text-2xl text-green-600">
            Edit Dashboard User
          </h1>
          {loading && (
            <div className="flex justify-center items-center h-[50vh]">
              <PulseLoader color="#3B82F6" loading={loading} size={15} />
            </div>
          )}
          {!loading && (
            <Box
              component="form"
              sx={{
                "& .MuiTextField-root": { m: 1, width: "40ch" },
              }}
              noValidate
              autoComplete="off"
              onSubmit={handleSubmit}
            >
              <div className="flex flex-col">
                <TextField
                  defaultValue={userDetails.user_fname}
                  onChange={(e) =>
                    setNewUserData({
                      ...newUserData,
                      user_fname: e.target.value,
                    })
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "#4CAF50",
                        color: "#4CAF50",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#4CAF50",
                    },
                  }}
                  name="user_fname"
                  label="First Name"
                />
                <TextField
                  defaultValue={userDetails.user_lname}
                  onChange={(e) =>
                    setNewUserData({
                      ...newUserData,
                      user_lname: e.target.value,
                    })
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "#4CAF50",
                        color: "#4CAF50",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#4CAF50",
                    },
                  }}
                  name="user_lname"
                  label="Last Name"
                />
                <div className="flex flex-col justify-center gap-2 w-[370px] mx-auto">
                  <Select
                    id="district"
                    multiple
                    disabled
                    sx={{ paddingX: "0px", height: "40px" }}
                    value={[...selectedDistrict]}
                    defaultValue={"Select Districts"}
                    renderValue={() => {
                      if (selectedDistrict.length !== 0) {
                        return (
                          <span className="text-gray-500">
                            {selectedDistrict[0]}
                          </span>
                        );
                      }
                    }}
                  ></Select>

                  {/* School types dropdown */}
                  <Select
                    className="form-control"
                    id="schoolType"
                    multiple
                    sx={{ paddingX: "0px", height: "40px" }}
                    defaultValue={"Select Districts"}
                    disabled
                    value={[...selectedSchoolType]}
                    renderValue={() => {
                      if (selectedSchoolType.length !== 0) {
                        return (
                          <span className="text-gray-500">
                            {selectedSchoolType[0]}
                          </span>
                        );
                      }
                    }}
                  ></Select>

                  {/* Schools dropdown */}
                  {schools && schools.length > 0 && (
                    <Autocomplete
                      options={schools}
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      fullWidth
                      value={
                        schools.find(
                          (school) => parseInt(school.id) === selectedSchool
                        ) || null
                      }
                      onChange={(e, option) => {
                        if (option === null) {
                          setSelectedSchool(null);
                          setViewAccessList({
                            ...viewAccessList,
                            schoolID: null,
                          });
                        } else {
                          setSelectedSchool(parseInt(option.id));
                          setViewAccessList({
                            ...viewAccessList,
                            schoolID: parseInt(option.id),
                          });
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          fullWidth
                          sx={{
                            "& .MuiInputBase-root": {
                              height: "40px",
                              padding: "0px",
                              width: "370px",
                              marginLeft: "-8px",
                              marginTop: "-8px",
                            },
                            "& .MuiInputBase-input": {
                              "&::placeholder": {
                                fontSize: "16px",
                                fontWeight: "500",
                                padding: "0 4px",
                              },
                            },
                          }}
                          {...params}
                          variant="outlined"
                          placeholder="Select School"
                        />
                      )}
                    />
                  )}
                </div>
              </div>
              <FormControl
                component="fieldset"
                variant="standard"
                className="mt-2 ml-2"
              >
                {userDetails.view_access_list && (
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={viewAccessList.smartclass}
                          onChange={handleChange}
                          name="smartclass"
                        />
                      }
                      label="Smartclass access"
                    />
                    <FormControlLabel
                      label="Mathlab access"
                      control={
                        <Switch
                          checked={viewAccessList.mathlab}
                          onChange={handleChange}
                          name="mathlab"
                        />
                      }
                    />
                    <FormControlLabel
                      label="isWrite"
                      control={
                        <Switch
                          checked={viewAccessList.isWrite}
                          onChange={handleChange}
                          name="isWrite"
                        />
                      }
                    />
                    <FormControlLabel
                      label="isEvaluator"
                      control={
                        <Switch
                          checked={viewAccessList.isEvaluator}
                          onChange={handleChange}
                          name="isEvaluator"
                        />
                      }
                    />
                  </FormGroup>
                )}
              </FormControl>
              <div className="w-full flex justify-center p-10">
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    backgroundColor: "#4CAF50",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "#45a049",
                    },
                  }}
                >
                  Submit
                </Button>
              </div>
            </Box>
          )}
        </Paper>
      </Modal>
    </div>
  );
};
